import {
  Box,
  Button,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
} from "@chakra-ui/react"
import { routing } from "@i18n/routing"
import { useRouter } from "next/router"
import { useMemo } from "react"
import { FaGlobe } from "react-icons/fa"

// Normalise BCP-47 tags and parse parts
function normalise(tag: string): string {
  try {
    return new Intl.Locale(tag).toString()
  } catch {
    const parts = tag.split("-")
    const lang = parts[0]?.toLowerCase() ?? tag
    const rest = parts
      .slice(1)
      .map((p) =>
        p.length === 4
          ? p[0].toUpperCase() + p.slice(1).toLowerCase()
          : p.toUpperCase()
      )
    return [lang, ...rest].join("-")
  }
}

// Autonym label: native name, plus script/region if present
function autonymFor(code: string): string {
  const norm = normalise(code)
  let language = norm,
    region: string | undefined,
    script: string | undefined

  try {
    const loc = new Intl.Locale(norm)
    language = loc.language || norm
    region = loc.region
    script = loc.script
  } catch {
    /* noop */
  }

  try {
    const dnLang = new Intl.DisplayNames([norm], { type: "language" })
    let label = dnLang.of(language) || code

    if (script) {
      const dnScript = new Intl.DisplayNames([norm], { type: "script" })
      const scriptName = dnScript.of(script)
      if (scriptName) label += ` (${scriptName})`
    }

    if (region) {
      const dnRegion = new Intl.DisplayNames([norm], { type: "region" })
      const regionName = dnRegion.of(region)
      if (regionName) label += ` (${regionName})`
    }

    return label
  } catch {
    // Minimal fallback
    const FALLBACK: Record<string, string> = {
      en: "English",
      ar: "العربية",
      de: "Deutsch",
      "es-MX": "Español (México)",
    }
    return FALLBACK[norm] || code
  }
}

const LanguagePicker = () => {
  const router = useRouter()
  const { pathname, query, asPath, locale, locales } = router

  const shortCode = useMemo(
    () => (locale || routing.defaultLocale).split("-")[0].toUpperCase(),
    [locale]
  )

  const options = useMemo(
    () => (locales ?? []).filter((l) => l !== "default"),
    [locales]
  )

  const handleSelect = async (next: string) => {
    if (!next || next === locale) return
    document.cookie = `NEXT_LOCALE=${next}; max-age=31536000; path=/`
    await router.push({ pathname, query }, asPath, { locale: next })
  }

  return (
    <Menu closeOnSelect>
      <MenuButton
        as={Button}
        size={"sm"}
        px={2}
        minW={"auto"}
        aria-label={"Change language"}
        color={"brand.dark"}
      >
        <HStack spacing={1}>
          <Icon
            as={FaGlobe}
            w={6}
            h={6}
          />
          <Text
            as={"span"}
            fontWeight={500}
          >
            {shortCode}
          </Text>
        </HStack>
      </MenuButton>

      <MenuList>
        <MenuOptionGroup
          type={"radio"}
          value={locale}
        >
          {options.map((l) => (
            <MenuItemOption
              key={l}
              value={l}
              onClick={() => handleSelect(l)}
            >
              <HStack
                justify={"space-between"}
                w={"full"}
              >
                <Text>{autonymFor(l)}</Text>
                <Box
                  as={"span"}
                  opacity={0.6}
                  fontSize={"sm"}
                >
                  {normalise(l)}
                </Box>
              </HStack>
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  )
}

export default LanguagePicker
